import { yearMonthParser } from "@validereinc/utilities";
import { isYearMonth } from "@validereinc/utilities";
import { endOfMonth, startOfMonth } from "date-fns";

/**
 * Reverses the backend's mongo filters to be the standard format for frontend filters. A simple reverse of `getFilterObject` but is not tested for all possible cases.
 * This function handles specific cases primarily for root level keys and `$and` operator for year_months.
 *
 * @example
 * // Input
 * const filters = {
 *   $and: [
 *     { year_month: { $gte: "202301" } },
 *     { year_month: { $lte: "202312" } }
 *   ],
 *   status: { $in: ["active"] },
 *   name: { $eq: "John"},
 *   age: 22
 * };
 *
 * Output:
 * normalizeFilters(filters);
 * Returns: { year_month: { from: "202301", to: "202312" }, status: ["active"], name: "John", age: 22 }
 *
 * @param input - MongoDB-like filter object
 * @returns A normalized object with simplified filter structure
 */
export const normalizeFilters = (
  input: Record<string, any>
): Record<string, any> => {
  const result: Record<string, any> = {};

  const mergeCondition = (field: string, condition: any) => {
    if (!(field in result)) {
      result[field] = {};
    }

    for (const op in condition) {
      const value = condition[op];

      if (["$eq", "$like", "$in"].includes(op)) {
        result[field] = value;
      } else if (op === "$gte") {
        result[field] = {
          ...(typeof result[field] === "object" ? result[field] : {}),
          from: value,
        };
      } else if (op === "$lte") {
        result[field] = {
          ...(typeof result[field] === "object" ? result[field] : {}),
          to: value,
        };
      } else {
        result[field][op] = value;
      }
    }
  };

  const processClause = (clause: any) => {
    for (const key in clause) {
      const value = clause[key];

      if (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value)
      ) {
        const isOperatorObject = Object.keys(value).some((k) =>
          k.startsWith("$")
        );
        if (isOperatorObject) {
          mergeCondition(key, value);
        } else {
          result[key] = value;
        }
      } else {
        result[key] = value;
      }
    }
  };

  if ("$and" in input && Array.isArray(input.$and)) {
    input.$and.forEach(processClause);
  }

  // Now process remaining top-level keys that aren't $and
  for (const key in input) {
    if (key !== "$and") {
      processClause({ [key]: input[key] });
    }
  }

  return result;
};

/**
 * Converts year-month string values (format: YYYYMM) in an object to ISO date strings.
 *
 * This function recursively traverses the object/array and replaces year-month strings:
 * - When the key is "from", converts to start of month ISO string
 * - When the key is "to", converts to end of month ISO string
 *
 * @example
 * // Input
 * const obj = { from: "202301", to: "202302", other: "value" };
 *
 *  After calling the function
 * convertYearMonthsToISO(obj);
 *  obj is now:
 *  {
 *    from: "2023-01-01T00:00:00.000Z",
 *    to: "2023-02-28T23:59:59.999Z",
 *    other: "value"
 *  }
 *
 * @param obj - The object or array of objects to process
 */
export const convertYearMonthsToISO = (
  obj: Record<string, any> | Array<Record<string, any>>
) => {
  if (Array.isArray(obj)) {
    obj.forEach((item) => convertYearMonthsToISO(item));
  } else if (obj && typeof obj === "object") {
    for (const [key, value] of Object.entries(obj)) {
      if (isYearMonth(value)) {
        if (key === "to") {
          obj[key] = endOfMonth(yearMonthParser(value)).toISOString();
        }
        if (key === "from") {
          obj[key] = startOfMonth(yearMonthParser(value)).toISOString();
        }
      }

      // Recurse into nested objects or arrays
      convertYearMonthsToISO(value);
    }
  }
};
